import apiClient from './client';
import { clearAllDrafts } from '@/hooks/autosaveRegistry';
 
import type { User } from '@/models';

export interface LoginRequest {
  user_email: string;
  user_password: string;
}

export interface RegisterRequest {
  user_email: string;
  user_password: string;
  user_name: string;
  user_phone?: string;
  website?: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export interface RegisterResponse {
  uuid: string;
  user_email?: string;
  userEmail?: string;
  user_name?: string;
  userName?: string;
  requiresEmailVerification?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ApiSuccessResponse<T = any> {
  message_key?: string;
  data?: T;
}

export interface ApiErrorResponse {
  error: {
    code: string;
    message_key: string;
    message?: string;
    errors?: Record<string, string[]>;
  };
}

// Response can be either:
// - Direct data (T) for success responses
// - Wrapped in { data: T, message_key?: string } if metadata is needed
// - { error: {...} } for error responses
export type ApiResponse<T = unknown> = T | ApiSuccessResponse<T> | ApiErrorResponse;

/**
 * True when the response is the `{ data: T }` wrapped shape rather than a
 * direct T payload. Narrows via an explicit predicate because `T`'s own
 * shape can otherwise overlap structurally with `ApiSuccessResponse<T>`.
 */
function isWrappedResponse<T>(response: T | ApiSuccessResponse<T>): response is ApiSuccessResponse<T> {
  return (
    typeof response === 'object' &&
    response !== null &&
    'data' in response &&
    (response as ApiSuccessResponse<T>).data !== undefined
  );
}

/**
 * Superadmin login (only allowed site administrators; no password reset for this entry point).
 * Uses same storage and token as login; redirect to management is handled by caller.
 */
export async function superadminLogin(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/superadmin/login', credentials);
  const data = response.data;

  if ('error' in data) {
    throw new Error((data as ApiErrorResponse).error?.message || 'Access denied');
  }

  const loginData = data as LoginResponse;
  if (!loginData || !loginData.access_token) {
    throw new Error('Invalid response from server');
  }

  sessionStorage.setItem('access_token', loginData.access_token);
  sessionStorage.setItem('user', JSON.stringify(loginData.user));
  localStorage.setItem('access_token', loginData.access_token);
  localStorage.setItem('user', JSON.stringify(loginData.user));
  apiClient.defaults.headers.common.Authorization = `Bearer ${loginData.access_token}`;

  return loginData;
}

/**
 * Login user
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  try {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/login', credentials);
    const data = response.data;
    
    
    // Check if response is an error
    if ('error' in data) {
      console.error('Error in login response:', data);
      throw new Error(data.error?.message || 'Invalid response from server');
    }
    
    // Response is direct login object (RESTful), but also accept the wrapped shape
    const loginData = isWrappedResponse(data) ? data.data : data;

    if (!loginData || !loginData.access_token) {
      console.error('Invalid login response:', data);
      throw new Error('Invalid response from server');
    }

    // Store token in sessionStorage and localStorage (fallback for reloads)
    sessionStorage.setItem('access_token', loginData.access_token);
    sessionStorage.setItem('user', JSON.stringify(loginData.user));
    localStorage.setItem('access_token', loginData.access_token);
    localStorage.setItem('user', JSON.stringify(loginData.user));
    // Ensure axios has the token immediately for subsequent requests
    apiClient.defaults.headers.common.Authorization = `Bearer ${loginData.access_token}`;
    
    return loginData;
  } catch (error: unknown) {
    console.error('Login API error:', error);
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: unknown } };
      console.error('Error response:', axiosError.response?.data);
    }
    throw error;
  }
}

/**
 * Register new user
 */
export async function register(userData: RegisterRequest): Promise<RegisterResponse> {
  const response = await apiClient.post<ApiResponse<RegisterResponse>>('/auth/register', userData);
  const data = response.data;
  
  // Check if response is an error
  if ('error' in data) {
    throw new Error(data.error?.message || 'Registration failed');
  }
  
  // Extract data - could be direct or wrapped
  const registerData = isWrappedResponse(data) ? data.data : data;
  
  if (!registerData) {
    throw new Error('Invalid response from server');
  }
  
  return registerData;
}

/**
 * Verify email token (unauthenticated)
 */
export async function verifyEmail(token: string, email?: string): Promise<{ emailVerified: boolean; userEmail?: string }> {
  const response = await apiClient.post<ApiResponse<{ emailVerified: boolean; userEmail?: string }>>('/auth/verify-email', {
    token,
    ...(email ? { email } : {}),
  });
  const data = response.data;
  if ('error' in data) {
    throw new Error(data.error?.message || 'Email verification failed');
  }
  const verifyData = isWrappedResponse(data) ? data.data : data;
  return verifyData ?? { emailVerified: true };
}

/**
 * Resend email verification link
 */
export async function resendVerificationEmail(user_email: string): Promise<{ sent: boolean }> {
  const response = await apiClient.post<ApiResponse<{ sent: boolean }>>('/auth/email/resend', {
    user_email,
  });
  const data = response.data;
  if ('error' in data) {
    throw new Error(data.error?.message || 'Failed to resend verification email');
  }
  const resendData = isWrappedResponse(data) ? data.data : data;
  return resendData ?? { sent: true };
}

/**
 * Confirm password reset with token and new password
 */
export async function confirmPasswordReset(payload: {
  token: string;
  user_email: string;
  user_password: string;
}): Promise<void> {
  const response = await apiClient.post<ApiResponse<null>>('/auth/reset-password/confirm', payload);
  const data = response.data;
  if (data && typeof data === 'object' && 'error' in data) {
    throw new Error((data as ApiErrorResponse).error?.message || 'Password reset failed');
  }
}

/**
 * Initiate social login (redirects to provider)
 */
export function initiateSocialLogin(provider: 'google' | 'facebook'): void {
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'https://jubiel.local/api';
  // Get current locale from URL
  const currentPath = window.location.pathname;
  const localeMatch = currentPath.match(/^\/(ro|en)/);
  const locale = localeMatch ? localeMatch[1] : 'ro';
  
  window.location.href = `${apiBaseUrl}/auth/social/${provider}?locale=${locale}`;
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout');
  } catch (error) {
    // Even if logout fails on server, clear local storage
    console.error('Logout error:', error);
  } finally {
    // Clear sessionStorage + localStorage
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('user');
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    delete apiClient.defaults.headers.common.Authorization;
    // JUB-152: unsaved editor drafts never survive into another session.
    clearAllDrafts();
  }
}

/**
 * Get current user from sessionStorage
 */
export function getCurrentUser(): User | null {
  const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

/**
 * Get access token from sessionStorage
 */
export function getAccessToken(): string | null {
  return sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getAccessToken();
}
